'use client';

import { ReactNode, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { observer } from 'mobx-react-lite';
import EmptySearch from 'components/EmptySearch/EmptySearch';
import ButtonShowMore from 'components/ButtonShowMore/ButtonShowMore';
import { SearchEventResult } from 'searchEvent.d';
import { SearchPlacesResult } from 'searchPlaces.d';
import { SearchProducersResult } from 'searchProducers.d';
import { LIMITS } from 'constants/index';
import locationStore from 'stores/LocationStore';
import Pagination from '@sympla/sympla-components/Pagination';
import SearchResultInfo from 'components/SearchResultInfo/SearchResultInfo';

const PaginationExtraLabel = dynamic(() => import('components/Shared/PaginationExtraLabel/PaginationExtraLabel'), {
  ssr: false,
});

type SearchContentProps = {
  children: ReactNode;
  pageTypeText: string;
  searchResult: SearchEventResult | SearchPlacesResult | SearchProducersResult;
  otherSearchResult?: SearchEventResult;
  totalPages: number;
  isMobile: boolean;
  isFetching?: boolean;
  onChangePage: (page: number | string) => number;
};

function SearchContent({
  children,
  pageTypeText,
  searchResult,
  otherSearchResult,
  totalPages,
  isFetching,
  onChangePage,
  isMobile,
}: SearchContentProps) {
  const { data, page, total } = searchResult;

  const extraLabel = useMemo(() => (isMobile ? <PaginationExtraLabel currentResults={data?.length} total={total} /> : <div />), [data, total]);

  const showPagination = (!!data?.length && !otherSearchResult?.data?.length) || (!data?.length && !!otherSearchResult?.data?.length);

  return (
    <>
      <SearchResultInfo
        pageTypeText={pageTypeText}
        numberOfEvents={total}
        currentPage={page || 1}
        totalPages={totalPages}
        location={locationStore.selectedLocation}
      />

      {data?.length || otherSearchResult?.data?.length ? (
        <>
          {!data?.length ? <EmptySearch hasOtherResult /> : null}
          {children}
        </>
      ) : (
        <EmptySearch />
      )}

      {showPagination && (
        <Pagination
          isLoading={isFetching}
          lang="pt"
          totalPages={totalPages || 1}
          currentPage={Number(page) || otherSearchResult?.page || 1}
          onChangePage={onChangePage}
          extraLabel={extraLabel}
          isMobile={isMobile}
        />
      )}

      {!!data?.length && !!otherSearchResult?.data?.length && otherSearchResult?.data?.length >= LIMITS.EVENTS_OTHER_CITIES && <ButtonShowMore />}
    </>
  );
}

export default observer(SearchContent);
